'use client'

import { Breakpoints } from 'fukku/styles/breakpoints'
import { imagesSizesByLayout } from 'plp/constants/grid'
import { useScreenSize } from 'plp/hooks/useScreenSize'
import type { ColumnsNumber } from 'plp/types/Options'
import { useProductCardConsumerLayoutContext } from 'product-card/hooks/useProductCardConsumerLayoutContext'
import { useProductCardImagesSizesContext } from 'product-card/hooks/useProductCardImagesSizesContext'
import { ColumnLayout } from 'product-card/types'
import {
	type CSSProperties,
	createContext,
	useEffect,
	useMemo,
	useState,
} from 'react'
import {
	getFromLocalStorage,
	setToLocalStorage,
} from 'utils/webStorage/localStorage'

import type { FiltersSearchParams } from '../types/Catalog'
import {
	type ColumnUserStore,
	DEFAULT_COLUMNS_LARGE,
	DEFAULT_COLUMNS_SMALL,
	GRID_COLUMNS_VALUES,
	LARGE,
	MAX_COLUMNS,
	PLP_USER_COLUMNS,
	SMALL,
	defaultStore,
	getDefaultStorageIndex,
	responsiveBehavior,
} from './utils'

export interface PlpContextType {
	columns: ColumnsNumber
	setNewColumns: (columns: ColumnsNumber) => void
	catalogId: string
	seoId: string
	defaultFilter: FiltersSearchParams | undefined
	gridCssVariables?: CSSProperties
}

const defaultContext: PlpContextType = {
	columns: ColumnLayout.TwoColumns as ColumnsNumber,
	setNewColumns: () => {},
	defaultFilter: undefined,
	catalogId: '',
	seoId: '',
}

export const PlpContext = createContext<PlpContextType>(defaultContext)

interface PlpProviderProps {
	catalogId: string
	seoId: string
	defaultFilter?: FiltersSearchParams
	children: (props: PlpContextType) => React.ReactNode
}

export const PlpProvider = ({
	catalogId,
	seoId,
	defaultFilter,
	children,
}: PlpProviderProps) => {
	const { showAsMediumOrSmall } = useScreenSize()

	const { setImagesSizes } = useProductCardImagesSizesContext()
	const { setColumnsLayout } = useProductCardConsumerLayoutContext()

	const defaultSelection = showAsMediumOrSmall
		? DEFAULT_COLUMNS_SMALL
		: DEFAULT_COLUMNS_LARGE

	const [columns, setColumns] = useState<ColumnsNumber>(defaultSelection)
	const [initialColumnsRender, setInitialColumnsRender] = useState(false)

	const setNewColumns = (newColumns: ColumnsNumber) => {
		const storage = getFromLocalStorage<ColumnUserStore>(PLP_USER_COLUMNS)
		if (storage) {
			storage[getDefaultStorageIndex(showAsMediumOrSmall)] = newColumns
			setToLocalStorage(PLP_USER_COLUMNS, storage)
			setImagesSizes(imagesSizesByLayout[newColumns])
			setColumns(newColumns)
		}
	}

	const contextState: PlpContextType = useMemo(
		() => ({
			columns,
			setNewColumns,
			catalogId,
			seoId,
			defaultFilter,
			gridCssVariables: {
				'--grid-columns': GRID_COLUMNS_VALUES[columns].toString(),
				'--grid-container':
					columns === MAX_COLUMNS ? '100vw' : `${Breakpoints.extraLarge}px`,
				'--grid-padding': columns === MAX_COLUMNS ? '0' : '12rem',
			} as CSSProperties,
		}),
		[columns, setColumns, defaultFilter, catalogId, seoId]
	)

	useEffect(() => {
		if (initialColumnsRender) {
			const numberColumns = responsiveBehavior[
				showAsMediumOrSmall ? SMALL : LARGE
			][columns] as ColumnsNumber
			setNewColumns(numberColumns)
		}

		let storage = getFromLocalStorage<ColumnUserStore>(PLP_USER_COLUMNS)
		if (!storage) {
			setToLocalStorage(PLP_USER_COLUMNS, defaultStore)
			storage = getFromLocalStorage<ColumnUserStore>(PLP_USER_COLUMNS)
		}
		const userSelection = storage?.[getDefaultStorageIndex(showAsMediumOrSmall)]
		const columnsNumber = userSelection ?? defaultSelection

		setColumns(columnsNumber)
		setImagesSizes(imagesSizesByLayout[columnsNumber])
		setInitialColumnsRender(true)
	}, [showAsMediumOrSmall])

	useEffect(() => {
		setColumnsLayout?.(columns)
	}, [columns])

	return (
		<PlpContext.Provider value={contextState}>
			{children(contextState)}
		</PlpContext.Provider>
	)
}
